import { useCallback, useEffect, useState, type FormEvent } from "react";
import { EmailInput } from "../components/MobileFields";
import { PasswordField } from "../components/PasswordField";
import { type Developer, devApi } from "../developer/api";
import { usePageRefresh } from "../lib/page-lifecycle";

type SmtpPayload = {
  host: string;
  port: number;
  username: string;
  from_email: string;
  from_name: string;
  use_tls: boolean;
  use_ssl: boolean;
  password_set: boolean;
  source: "saved" | "env" | "none";
  configured: boolean;
};

type SmtpState = {
  smtp: SmtpPayload;
  defaults: { host: string; port: number; useTls: boolean; useSsl: boolean; fromName: string };
};

export function SmtpSettingsForm({ me }: { me: Developer }) {
  const [host, setHost] = useState("smtp.gmail.com");
  const [port, setPort] = useState("587");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("Dashcam");
  const [useTls, setUseTls] = useState(true);
  const [useSsl, setUseSsl] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [source, setSource] = useState<SmtpPayload["source"]>("none");
  const [testTo, setTestTo] = useState(me.email || "");
  const [busy, setBusy] = useState<"save" | "test" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testOk, setTestOk] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const body = await devApi<SmtpState>("/api/developers/smtp");
      const smtp = body.smtp;
      setHost(smtp.host || body.defaults.host);
      setPort(String(smtp.port || body.defaults.port));
      setUsername(smtp.username);
      setFromEmail(smtp.from_email);
      setFromName(smtp.from_name || body.defaults.fromName);
      setUseTls(smtp.use_tls);
      setUseSsl(smtp.use_ssl);
      setPasswordSet(smtp.password_set);
      setSource(smtp.source);
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load SMTP settings");
      setLoaded(true);
    }
  }, []);

  usePageRefresh(
    useCallback(async () => {
      if (busy) return;
      await loadSettings();
    }, [busy, loadSettings]),
  );

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const save = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy("save");
    setError(null);
    setOk(null);
    try {
      const body = await devApi<{ smtp: SmtpPayload }>("/api/developers/smtp", {
        method: "PUT",
        body: JSON.stringify({
          host,
          port: Number(port),
          username,
          password: password || undefined,
          from_email: fromEmail,
          from_name: fromName,
          use_tls: useTls,
          use_ssl: useSsl,
        }),
      });
      setPassword("");
      setPasswordSet(body.smtp.password_set);
      setSource(body.smtp.source);
      setOk("SMTP settings saved. Test mail uses this configuration.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save SMTP settings");
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async (ev: FormEvent) => {
    ev.preventDefault();
    setBusy("test");
    setTestError(null);
    setTestOk(null);
    try {
      const body = await devApi<{ message?: string }>("/api/developers/smtp/test", {
        method: "POST",
        body: JSON.stringify({ to: testTo.trim() }),
      });
      setTestOk(body.message || `Test email sent to ${testTo.trim()}.`);
    } catch (err) {
      setTestError(err instanceof Error ? err.message : "Could not send the test email");
    } finally {
      setBusy(null);
    }
  };

  const toggleTls = (on: boolean) => {
    setUseTls(on);
    if (on) {
      setUseSsl(false);
      if (port === "465") setPort("587");
    }
  };

  const toggleSsl = (on: boolean) => {
    setUseSsl(on);
    if (on) {
      setUseTls(false);
      if (port === "587") setPort("465");
    }
  };

  return (
    <>
      <p className="devportal-hint">
        Saved SMTP settings are used for every outgoing email, including Change Email OTPs. The password is stored encrypted and is never shown again.
        {source === "env" ? " Environment variables are in use until you save settings here." : null}
        {source === "none" ? " Defaults below match smtp.gmail.com on port 587 with TLS." : null}
      </p>
      <form className="device-form" onSubmit={(ev) => void save(ev)}>
        <label>
          SMTP host
          <input
            value={host}
            onChange={(e) => setHost(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            name="smtp-host"
            required
            disabled={!loaded || busy != null}
          />
        </label>
        <label>
          SMTP port
          <input
            value={port}
            onChange={(e) => setPort(e.target.value.replace(/\D/g, "").slice(0, 5))}
            inputMode="numeric"
            name="smtp-port"
            required
            disabled={!loaded || busy != null}
          />
        </label>
        <label>
          SMTP email / username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            name="smtp-username"
            disabled={!loaded || busy != null}
          />
        </label>
        <label>
          SMTP password
          <PasswordField
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={passwordSet ? "Leave blank to keep the saved password" : "App password or SMTP password"}
            disabled={!loaded || busy != null}
          />
        </label>
        <label>
          SMTP from name
          <input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            name="smtp-from-name"
            required
            disabled={!loaded || busy != null}
          />
        </label>
        <label>
          SMTP from email
          <EmailInput
            autoComplete="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            required
            disabled={!loaded || busy != null}
            name="smtp-from-email"
          />
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={useTls} onChange={(e) => toggleTls(e.target.checked)} disabled={!loaded || busy != null} />
          Use TLS (STARTTLS, typically port 587)
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={useSsl} onChange={(e) => toggleSsl(e.target.checked)} disabled={!loaded || busy != null} />
          Use SSL (implicit TLS, typically port 465)
        </label>
        {error && <p className="form-error">{error}</p>}
        {ok && <p className="form-success">{ok}</p>}
        <button type="submit" className="primary" disabled={!loaded || busy != null}>
          {busy === "save" ? "Saving…" : "Save SMTP Settings"}
        </button>
      </form>

      <form className="device-form smtp-test-form" onSubmit={(ev) => void sendTest(ev)}>
        <p className="devportal-hint">Send a test message with the currently saved SMTP configuration.</p>
        <label>
          Test recipient
          <EmailInput
            autoComplete="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            required
            disabled={busy != null}
            name="smtp-test-to"
          />
        </label>
        {testError && <p className="form-error">{testError}</p>}
        {testOk && <p className="form-success">{testOk}</p>}
        <button type="submit" className="primary" disabled={busy != null || source !== "saved"}>
          {busy === "test" ? "Sending…" : "Send Test Mail"}
        </button>
      </form>
    </>
  );
}
